/**
 * TemplateEditDialog — edit a refusal template's body/future_hint/is_active.
 *
 * Body supports {member_first_name} {query_topic_paraphrase}
 * {redirect_suggestion} — clicking a variable chip inserts it at the
 * textarea cursor (falls back to append-at-end, which is fine per the brief).
 */

import React, { useRef, useState } from 'react';
import { Box, Typography, Chip, Switch, FormControlLabel, Alert, TextField } from '@mui/material';
import AdminEditDialogShell from '../../shared/AdminEditDialogShell';
import PromptField from '../../shared/PromptField';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors } from '../../../theme/designTokens';
import { RefusalTemplate, UpdateRefusalTemplateRequest } from '../../../services/quillAdminService';

const ACCENT = colors.warning.main;
const VARIABLES = ['{member_first_name}', '{query_topic_paraphrase}', '{redirect_suggestion}'];

interface TemplateEditDialogProps {
  template: RefusalTemplate;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (patch: UpdateRefusalTemplateRequest) => Promise<void> | void;
}

const TemplateEditDialog: React.FC<TemplateEditDialogProps> = ({ template, open, saving, onClose, onSave }) => {
  const [body, setBody] = useState(template.body);
  const [futureHint, setFutureHint] = useState(template.future_hint ?? '');
  const [isActive, setIsActive] = useState(template.is_active);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isDirty = body !== template.body || futureHint !== (template.future_hint ?? '') || isActive !== template.is_active;

  const insertVariable = (variable: string) => {
    const el = textareaRef.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      const next = body.slice(0, start) + variable + body.slice(end);
      setBody(next);
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + variable.length;
      });
    } else {
      setBody((prev) => `${prev}${variable}`);
    }
  };

  const handleSaveClick = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    await onSave({ body, future_hint: futureHint || undefined, is_active: isActive });
    setConfirmOpen(false);
  };

  return (
    <>
      <AdminEditDialogShell
        open={open}
        onClose={onClose}
        accentColor={ACCENT}
        typeInitial="T"
        title={template.template_key}
        subtitle="Refusal template"
        onSave={handleSaveClick}
        isSaving={saving}
        isDirty={isDirty}
        maxWidth="md"
      >
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This text is shown verbatim to members when a request is refused (production).
          </Alert>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Insert variable</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {VARIABLES.map((v) => (
              <Chip key={v} label={v} size="small" onClick={() => insertVariable(v)} sx={{ fontFamily: 'monospace', cursor: 'pointer' }} />
            ))}
          </Box>

          <Box sx={{ mb: 3 }}>
            <PromptField
              label="Body"
              value={body}
              onChange={setBody}
              inputRef={textareaRef}
              accentColor={ACCENT}
              helperText="Shown verbatim to the member. Supports {member_first_name}, {query_topic_paraphrase}, {redirect_suggestion}."
              minRows={6}
            />
          </Box>

          <TextField
            label="Future hint"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={futureHint}
            onChange={(e) => setFutureHint(e.target.value)}
            helperText="Optional internal note about when/how this refusal should evolve"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Template is active"
          />
        </Box>
      </AdminEditDialogShell>

      <QuillActionConfirm
        open={confirmOpen}
        title="Save refusal template"
        message="This text is shown verbatim to members when a request is refused (production)."
        confirmLabel="Confirm & Save"
        loading={saving}
        pendingLabel="Applying…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default TemplateEditDialog;
